import React, { useState, useEffect } from "react";
import { FEATURES } from "@/config/features";
import AIInlineRail from "./AIInlineRail";
import AIInlineMenu from "./AIInlineMenu";
import { useAICommands } from "./hooks/useAICommands";
import type { ImportantData } from '@/store/aiDataStore';

type Props = {
  editorRef: React.RefObject<HTMLElement>;
  documentId?: string;
  casoId?: string; // ✅ NOVO
  onLoadKBData?: () => Promise<void>; // ✅ NOVO
};

export function AIInlineIntegration({ editorRef, documentId, casoId, onLoadKBData }: Props) {
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number; range: Range } | null>(null);
  const [slashMenuVisible, setSlashMenuVisible] = useState(false);
  const [slashMenuPosition, setSlashMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  
  const { 
    selectedText, 
    selectionRect, 
    isProcessingOCR,
    isProcessingTone,
    continueWriting, 
    refineStructure, 
    insertSummary, 
    openInlineChat,
    addDataFromFiles,
    insertCandidate,
    getCandidatesForSelection,
    adjustTone
  } = useAICommands({
    editorRef,
    onMenuShow: (position) => {
      setSlashMenuPosition(position);
      setSlashMenuVisible(true);
    },
  });

  const hasSelection = selectedText.trim().length > 0;

  // Lifecycle management para limpeza de listeners
  useEffect(() => {
    // Cleanup quando o componente unmount ou editor ref muda
    return () => {
      // Remover qualquer marker temporário que possa ter ficado
      document.querySelectorAll('[data-ai-caret-marker]').forEach(el => el.remove());
    };
  }, [editorRef]);

  const handleMenuAction = (action: 'continue' | 'refine' | 'summary' | 'chat', range: Range) => {
    switch (action) {
      case 'continue':
        continueWriting(range);
        break;
      case 'refine':
        refineStructure(range);
        break;
      case 'summary':
        insertSummary(range);
        break;
      case 'chat':
        openInlineChat(range);
        break;
    }
  };

  const handleSelectionAction = (command: { id: string; label: string }) => {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    
    switch (command.id) {
      case 'refactor':
        if (range) refineStructure(range);
        break;
      case 'addData':
        addDataFromFiles();
        break;
      case 'summarize':
        if (range) insertSummary(range);
        break;
      case 'tone':
        // Tone adjustment is handled by the ToneAdjustmentPanel
        break;
    }
  };

  const handleManualInput = async ({ prompt, range }: { prompt: string; range: any }) => {
    console.log('[AIInlineRail][INTEGRATION] Recebendo input manual:', new Date().toISOString());
    console.log('[AIInlineRail][INTEGRATION] Prompt:', prompt?.substring(0, 50));
    console.log('[AIInlineRail][INTEGRATION] Range serializado:', !!range);
    
    if (!prompt.trim()) {
      console.warn('[AIInlineRail][INTEGRATION] ❌ Prompt vazio');
      return;
    }

    // Processar range ou dados serializados (correção definitiva)
    let targetRange: any = range;
    if (!targetRange) {
      console.log('[AIInlineRail][INTEGRATION] Criando range para seleção atual...');
      
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const currentRange = selection.getRangeAt(0);
        // Serializar range atual
        targetRange = {
          startContainer: currentRange.startContainer,
          startOffset: currentRange.startOffset,
          endContainer: currentRange.endContainer,
          endOffset: currentRange.endOffset,
          collapsed: currentRange.collapsed
        };
        console.log('[AIInlineRail][INTEGRATION] Range atual serializado:', { collapsed: currentRange.collapsed });
      } else if (editorRef.current) {
        console.log('[AIInlineRail][INTEGRATION] Criando range para fim do editor...');
        // Criar dados serializados para o final do editor se não houver seleção
        const newRange = document.createRange();
        newRange.selectNodeContents(editorRef.current);
        newRange.collapse(false);
        targetRange = {
          startContainer: newRange.startContainer,
          startOffset: newRange.startOffset,
          endContainer: newRange.endContainer,
          endOffset: newRange.endOffset,
          collapsed: newRange.collapsed
        };
      }
    }
    
    if (targetRange) {
      try {
        // Executar com prompt customizado usando dados serializados
        await continueWriting({ prompt, range: targetRange });
        console.log('[AIInlineRail][INTEGRATION] ✅ Input manual processado com sucesso');
      } catch (error) {
        console.error('[AIInlineRail][INTEGRATION] ❌ Erro no processamento:', error);
      }
    } else {
      console.error('[AIInlineRail][INTEGRATION] ❌ Não foi possível criar range válido');
    }
  };

  // Obter candidatos e dados importantes para a seleção atual
  const selectionData = hasSelection ? getCandidatesForSelection(selectedText) : { candidates: [], importantData: undefined, isUnknown: false };
  const showCandidates = selectionData.candidates.length > 0 || (selectionData.importantData ? Object.values(selectionData.importantData).some((arr: any) => arr && arr.length > 0) : false);

  const handleSlashMenuCommand = (command: any) => {
    setSlashMenuVisible(false);
    
    // Criar range no cursor atual
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    switch (command.id) {
      case 'suggest':
        continueWriting(range);
        break;
      case 'refactor':
        refineStructure(range);
        break;
      case 'expand':
        continueWriting(range);
        break;
      case 'summarize':
        insertSummary(range);
        break;
      case 'tone':
        openInlineChat(range);
        break;
    }
  };

  return (
    <>
      {/* Menu slash: visível quando "/" é digitado */}
      {slashMenuVisible && (
        <AIInlineMenu
          position={slashMenuPosition}
          visible={true}
          onSelectCommand={handleSlashMenuCommand}
          onClose={() => setSlashMenuVisible(false)}
        />
      )}
      
      {/* Rail IA: sempre visível quando editor focado */}
      {editorRef.current && (
        <AIInlineRail
          visible={true}
          selectedText={selectedText}
          isProcessingOCR={isProcessingOCR}
          isProcessingTone={isProcessingTone}
          onSelectCommand={handleSelectionAction}
          onClose={() => {
            // Rail não se fecha automaticamente
          }}
          candidates={selectionData.candidates}
          importantData={selectionData.importantData}
          isUnknownType={selectionData.isUnknown}
          onPickCandidate={(candidate) => {
            const selection = window.getSelection();
            const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
            if (range) {
              insertCandidate(candidate, range);
            }
          }}
          showCandidates={showCandidates}
          onManualInput={handleManualInput}
          documentId={documentId}
          onAdjustTone={(text, options) => adjustTone(text, options)}
          casoId={casoId}
          onLoadKBData={onLoadKBData}
        />
      )}
    </>
  );
}